<script setup>
import { onMounted, ref, computed } from 'vue';
import { uniqueId } from 'lodash-es';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';

const props = defineProps({
  ariaLabel: {
    type: String,
    default: '',
  },
  hasPagination: {
    type: Boolean,
    default: false,
  },
  hideFooter: {
    type: Boolean,
    default: false,
  },
  extraButtonClasses: {
    type: String,
    default: '',
  },
  sliderOptions: {
    type: Object,
    default: () => ({
      spaceBetween: 20,
      slidesPerView: 1,
      freeMode: false,
      breakpoints: {
        768: {
          slidesPerView: 3,
        },
      },
      mousewheel: {
        forceToAxis: true,
      },
      simulateTouch: false,
    }),
  },
});

const footerTypeClasses = computed(() => [
  { slider__pagination: props.hasPagination, slider__scrollbar: !props.hasPagination },
]);

const slider = ref(null);
const sliderRef = ref(null);
const id = ref(uniqueId('slider-'));

const resetPosition = () => {
  slider.value.slideTo(0);
};

onMounted(() => {
  if (sliderRef.value) {
    slider.value = Toolkit.slider(sliderRef.value, props.sliderOptions);
  }
});

defineExpose({
  resetPosition,
});
</script>

<template>
  <div
    :id="id"
    ref="sliderRef"
    class="slider"
    role="group"
    :aria-roledescription="$t('Accessibility.carouselRoleDescription')"
    :aria-label="ariaLabel"
  >
    <phxsg-button
      variant="secondary"
      icon-only
      class="slider__nav slider__nav--prev"
      :class="extraButtonClasses"
      :icon="{ id: 'chevron-left', size: 'lg', bidirectional: true }"
      :aria-label="$t('Button.previousSlide')"
      :label="$t('Button.previousSlide')"
    />

    <div class="slider__body" aria-atomic="false" aria-live="polite">
      <slot :slider-id="id" />
    </div>

    <phxsg-button
      variant="secondary"
      icon-only
      class="slider__nav slider__nav--next"
      :class="extraButtonClasses"
      :icon="{ id: 'chevron-right', size: 'lg', bidirectional: true }"
      :aria-label="$t('Button.nextSlide')"
      :label="$t('Button.nextSlide')"
    />

    <div v-show="!hideFooter" class="slider__footer">
      <div :class="footerTypeClasses"></div>
    </div>
  </div>
</template>
